import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import axios from "axios";
import { server } from "../../server";
import { toast } from "react-toastify";
import { toggleAvatarDropdown } from "../../state/uiSlice";
import { loadSellers, setActiveAiMember, switchProfile } from "../../redux/actions/user";
import { clearAuthSession } from "../../utils/authSession";
import { setStoredCurrentProfile } from "../../utils/shopProfile";
import { buildPublicAppUrl } from "../../utils/publicApp";
import SharedRightSlideout from "./SharedRightSlideout";
import ShareToSocialMedia from "../Shop/ShareToSocialMedia";
import { getProfileAvatarUrl } from "../../utils/avatarUrl";
import { buildAiMemberProfileKey, isAiMemberProfile } from "../../utils/shopProfile";
import { CpuChipIcon } from "@heroicons/react/24/outline";

const DEFAULT_AVATAR_SVG = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 96 96"><rect width="96" height="96" rx="48" fill="#1f2937"/><circle cx="48" cy="36" r="18" fill="#d1d5db"/><path d="M18 82c5-20 17-30 30-30s25 10 30 30" fill="#d1d5db"/></svg>';
const DEFAULT_AVATAR_URL = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(DEFAULT_AVATAR_SVG)}`;

const AvatarDropdown = () => {
  const dispatch = useDispatch();
  const userProfile = useSelector((state) => state.user.user);
  const sellerProfiles = useSelector((state) => state.seller.sellers);
  const activeSellerProfile = useSelector((state) => state.seller.activeSeller);
  const sellerProfile = useSelector((state) => state.seller.seller);
  const currentProfile = useSelector((state) => state.user.currentProfile);
  const aiMembers = useSelector((state) => state.user.aiMembers || []);
  const activeAiMember = useSelector((state) => state.user.activeAiMember);
  const userSubscriptions = useSelector((state) => state.user.subscriptions || []);
  const sellerSubscriptions = useSelector((state) => state.seller.subscriptions || []);
  const isAvatarDropdownOpen = useSelector((state) => state.ui.isAvatarDropdownOpen);
  const isAdminUser = userProfile?.role === "Admin";

  // Determine the current profile's avatar
  const currentShopProfile =
    sellerProfiles.find((seller) => seller.name === currentProfile) ||
    (activeSellerProfile?.name === currentProfile ? activeSellerProfile : null) ||
    (sellerProfile?.name === currentProfile ? sellerProfile : null);

  const selectedAiMember = activeAiMember || aiMembers.find(
    (aiMember) => buildAiMemberProfileKey(aiMember) === currentProfile
  );
  const profile = currentProfile === "User"
    ? userProfile
    : isAiMemberProfile(currentProfile)
      ? selectedAiMember
      : currentShopProfile;

  const avatarUrl = getProfileAvatarUrl(profile, DEFAULT_AVATAR_URL);
  const hasShopProfiles = sellerProfiles.length > 0;
  const profileHandle = profile?.handle ? `@${profile.handle}` : "";
  const qrCodeUrl = profile?.qrCodeUrl || "";
  const qrCodeTargetUrl = profile?.qrCodeTargetUrl || "";
  const publicProfileUrl = qrCodeTargetUrl || (
    profile?.handle
      ? currentProfile === "User"
        ? buildPublicAppUrl(`/user/${profile.handle}`)
        : isAiMemberProfile(currentProfile)
          ? buildPublicAppUrl(`/ai/${profile.handle}`)
        : buildPublicAppUrl(`/shop/preview/${profile.handle}`)
      : buildPublicAppUrl("/")
  );

  useEffect(() => {
    if (userProfile && sellerProfiles.length === 0) {
      dispatch(loadSellers());
    }
  }, [dispatch, userProfile, sellerProfiles.length]);

  // Toggles the Redux state for avatar dropdown
  const handleToggleAvatarDropdown = () => {
    dispatch(toggleAvatarDropdown());
  };

  const handleProfileSwitch = (targetProfile) => {
    dispatch(switchProfile(targetProfile));
    handleToggleAvatarDropdown();
  };

  // Logout handler
  const logoutHandler = () => {
    axios
      .post(`${server}/user/logout`, {}, { withCredentials: true })
      .then((res) => {
        clearAuthSession();
        setStoredCurrentProfile("User");
        toast.success(res.data.message);
        setTimeout(() => {
          window.location.href = buildPublicAppUrl("/");
        }, 1000);
      })
      .catch((error) => {
        console.log(error?.response?.data?.message);
      });
  };

  // Get Subscription Icon
  const getSubscriptionImageUrl = () => {
    let maxTier = 0;

    if (currentProfile === "User" && userSubscriptions.length) {
      maxTier = userSubscriptions.reduce((max, sub) => {
        return sub.tier === "Super OG" ? 3 : sub.tier === "OG" ? 2 : max;
      }, 0);
    } else if (sellerSubscriptions.length) {
      maxTier = sellerSubscriptions.reduce((max, sub) => {
        return sub.tier === "Super OG Shop" ? 3 : sub.tier === "OG Shop" ? 2 : max;
      }, 0);
    }

    switch (maxTier) {
      case 3:
        return "https://res.cloudinary.com/dzlopmfj8/image/upload/v1715090157/super_og_youavf.svg";
      case 2:
        return "https://res.cloudinary.com/dzlopmfj8/image/upload/v1715090152/og_ircw65.svg";
      default:
        return null;
    }
  };

  const subscriptionIconUrl = getSubscriptionImageUrl();

  // If not open, render nothing
  if (!isAvatarDropdownOpen) return null;

  return (
    <SharedRightSlideout
      isOpen={isAvatarDropdownOpen}
      onClose={handleToggleAvatarDropdown}
      className="avatar-dropdown-shared-slideout"
      bodyClassName="avatar-dropdown-shared-slideout-body"
    >
        <div className="avatar-dropdown-menu-header">
          <img
            src={avatarUrl}
            alt={`${currentProfile} Avatar`}
            className="avatar-dropdown-menu-avatar"
            onError={(event) => {
              if (event.currentTarget.src !== DEFAULT_AVATAR_URL) {
                event.currentTarget.src = DEFAULT_AVATAR_URL;
              }
            }}
          />
        </div>

        <div className="avatar-dropdown-mode-selector" role="tablist" aria-label="Workspace">
          <Link
            to="/my-profile"
            className="avatar-dropdown-mode-option avatar-dropdown-mode-option-active"
            onClick={() => handleProfileSwitch("User")}
            role="tab"
          >
            Profile
          </Link>
          {isAdminUser && (
            <Link
              to="/admin/dashboard"
              className="avatar-dropdown-mode-option"
              onClick={handleToggleAvatarDropdown}
              role="tab"
            >
              Admin
            </Link>
          )}
        </div>

        {subscriptionIconUrl && (
          <div className="avatar-dropdown-menu-subscription-icon-container">
            <img
              src={subscriptionIconUrl}
              alt="Subscription Status"
              className="avatar-dropdown-menu-subscription-icon"
            />
          </div>
        )}

        <div className="avatar-dropdown-menu-items">
          <div className="avatar-dropdown-menu-item">
            <Link
              to="/my-profile"
              className="avatar-dropdown-menu-link"
              onClick={() => handleProfileSwitch("User")}
            >
              My Profile
            </Link>
          </div>

          {isAdminUser && <div className="avatar-dropdown-menu-item">
            <Link
              to="/ai-member-create"
              className="avatar-dropdown-menu-link"
              onClick={handleToggleAvatarDropdown}
            >
              Manage AI Members
            </Link>
          </div>}

          <div className="avatar-dropdown-menu-item">
            <Link
              to="/profile"
              className="avatar-dropdown-menu-link"
              onClick={() => handleProfileSwitch("User")}
            >
              Account Settings
            </Link>
          </div>

          <div className="avatar-dropdown-menu-item">
            <Link
              to="/shop-create"
              className="avatar-dropdown-menu-link"
              onClick={handleToggleAvatarDropdown}
            >
              Create a Shop
            </Link>
          </div>

          <div className="avatar-dropdown-menu-item">
            <button
              type="button"
              onClick={logoutHandler}
              className="avatar-dropdown-menu-link avatar-dropdown-menu-action-button"
            >
              Log Out
            </button>
          </div>
        </div>

        {aiMembers.length > 0 && (
          <div className="avatar-dropdown-menu-shops-section">
            <p className="avatar-dropdown-menu-section-label">My AI Members</p>
            <div className="avatar-dropdown-menu-shops-list">
              {aiMembers.map((aiMember) => {
                const profileKey = buildAiMemberProfileKey(aiMember);
                return (
                  <Link
                    key={profileKey}
                    to={`/ai/${aiMember.handle}`}
                    className={`avatar-dropdown-menu-shop-link ${currentProfile === profileKey ? "avatar-dropdown-menu-shop-link-active" : ""}`.trim()}
                    onClick={() => {
                      dispatch(setActiveAiMember(aiMember));
                      handleToggleAvatarDropdown();
                    }}
                  >
                    {aiMember.avatar?.url ? (
                      <img src={aiMember.avatar.url} alt={`${aiMember.name} Avatar`} className="avatar-dropdown-menu-shop-avatar" />
                    ) : (
                      <CpuChipIcon className="avatar-dropdown-menu-shop-avatar" aria-hidden="true" />
                    )}
                    <span className="avatar-dropdown-menu-shop-name">{aiMember.name}</span>
                  </Link>
                );
              })}
            </div>
            <div className="avatar-dropdown-menu-shops-divider" />
          </div>
        )}

        {(hasShopProfiles || isAdminUser) && (
          <div className="avatar-dropdown-menu-shops-section">
            <p className="avatar-dropdown-menu-section-label">{hasShopProfiles ? "My Shops" : "Admin"}</p>

            {hasShopProfiles && (
              <div className="avatar-dropdown-menu-shops-list">
                {sellerProfiles.map((seller) => {
                  const isActiveShop = currentProfile === seller.name;

                  return (
                    <Link
                      key={seller._id || seller.handle || seller.name}
                      to={`/shop/preview/${seller.handle}`}
                      className={`avatar-dropdown-menu-shop-link ${
                        isActiveShop ? "avatar-dropdown-menu-shop-link-active" : ""
                      }`.trim()}
                      onClick={() => handleProfileSwitch(seller.name)}
                    >
                      <img
                        src={seller.avatar?.url || avatarUrl}
                        alt={`${seller.name} Avatar`}
                        className="avatar-dropdown-menu-shop-avatar"
                        onError={(event) => {
                          if (event.currentTarget.src !== DEFAULT_AVATAR_URL) {
                            event.currentTarget.src = DEFAULT_AVATAR_URL;
                          }
                        }}
                      />
                      <span className="avatar-dropdown-menu-shop-name">{seller.name}</span>
                    </Link>
                  );
                })}
              </div>
            )}

            {isAdminUser && (
              <div className="avatar-dropdown-menu-admin-section">
                {hasShopProfiles && (
                  <p className="avatar-dropdown-menu-section-label avatar-dropdown-menu-admin-section-label">Admin Tools</p>
                )}
                <Link
                  to="/admin/dashboard"
                  className="avatar-dropdown-menu-shop-link avatar-dropdown-menu-admin-link"
                  onClick={handleToggleAvatarDropdown}
                >
                  <span className="avatar-dropdown-menu-admin-icon" aria-hidden="true">A</span>
                  <span className="avatar-dropdown-menu-shop-name">Admin Tools</span>
                </Link>
              </div>
            )}

            <div className="avatar-dropdown-menu-shops-divider" />
          </div>
        )}

        {qrCodeUrl && (
          <div className="avatar-dropdown-menu-qr-section">
            <p className="avatar-dropdown-menu-section-label">Share</p>

            <ShareToSocialMedia
              url={publicProfileUrl}
              message={`Check out ${profile?.name || currentProfile} on MATRIX.`}
              profileName={profile?.name || currentProfile}
              profileHandle={profile?.handle || ""}
              qrCodeUrl={qrCodeUrl}
              title={currentProfile === "User" ? "Share Profile" : isAiMemberProfile(currentProfile) ? "Share AI Member" : "Share Shop"}
            />

            <div className="avatar-dropdown-menu-qr-card">
              <div className="avatar-dropdown-menu-qr-copy">
                <p className="avatar-dropdown-menu-qr-title">Share</p>
                {profileHandle && (
                  <p className="avatar-dropdown-menu-qr-handle">{profileHandle}</p>
                )}
              </div>

              <img
                src={qrCodeUrl}
                alt={`${currentProfile} QR code`}
                className="avatar-dropdown-menu-qr-image"
              />

              {qrCodeTargetUrl && (
                <a
                  href={qrCodeTargetUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="avatar-dropdown-menu-qr-link"
                >
                  Open QR destination
                </a>
              )}
            </div>
          </div>
        )}
    </SharedRightSlideout>
  );
};

export default AvatarDropdown;





